import React, { useState, useRef } from "react";
import { Container, Box, Button, IconButton, Typography } from "@mui/material";
import { Youtube, X, ChevronLeft, ChevronRight } from "react-feather";
import { useTranslation } from "react-i18next";

/**
 * Extract YouTube video ID from various URL formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/live/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 */
const extractVideoId = (url) => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
};

const getThumbnail = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

const Video = ({ linkData }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState(null);
  const scrollRef = useRef(null);
  const { t } = useTranslation();

  if (!linkData?.length) return null;

  // Parse all valid videos
  const videos = linkData
    .map((item) => {
      const videoId = extractVideoId(item?.link);
      return videoId
        ? {
            id: videoId,
            title: item?.title || "",
            link: item?.link,
            embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
            thumbnail: getThumbnail(videoId),
          }
        : null;
    })
    .filter(Boolean);

  if (!videos.length) return null;

  const handlePlay = (index) => {
    setActiveIndex(index);
    setPlayingIndex(index);
  };

  const handleClose = () => setPlayingIndex(null);

  const handlePrev = () => {
    setPlayingIndex(null);
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : videos.length - 1));
  };

  const handleNext = () => {
    setPlayingIndex(null);
    setActiveIndex((prev) => (prev < videos.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (index) => {
    setPlayingIndex(null);
    setActiveIndex(index);
  };

  const current = videos[activeIndex];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Main Video Area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          pt: "56.25%",
          backgroundImage: `url(${current.thumbnail})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#000",
        }}
      >
        {playingIndex !== activeIndex && (
          <IconButton
            onClick={() => handlePlay(activeIndex)}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "primary.main",
              color: "white",
              width: 80,
              height: 80,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <Youtube size={48} />
          </IconButton>
        )}

        {playingIndex === activeIndex && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(0,0,0,0.8)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <iframe
              src={current.embedUrl}
              title={current.title || "YouTube video player"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: "90%",
                height: "80%",
                maxWidth: 900,
                border: "none",
                borderRadius: 8,
              }}
            />
            <Button
              onClick={handleClose}
              sx={{
                mt: 2,
                bgcolor: "white",
                color: "black",
                fontWeight: "bold",
                "&:hover": { bgcolor: "grey.200" },
              }}
              startIcon={<X size={15} />}
            >
              {t("close")}
            </Button>
          </Box>
        )}

        {/* Nav Arrows (show only if multiple videos) */}
        {videos.length > 1 && playingIndex !== activeIndex && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ChevronLeft size={28} />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                top: "50%",
                right: 12,
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ChevronRight size={28} />
            </IconButton>
          </>
        )}
      </Box>

      {/* Video counter */}
      {videos.length > 1 && (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 1, color: "text.secondary" }}
        >
          {activeIndex + 1} / {videos.length}
        </Typography>
      )}

      {/* Thumbnail Strip */}
      {videos.length > 1 && (
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 2,
            overflowX: "auto",
            pb: 1,
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "grey.400",
              borderRadius: 3,
            },
          }}
        >
          {videos.map((video, index) => (
            <Box
              key={video.id}
              onClick={() => handleThumbnailClick(index)}
              sx={{
                flexShrink: 0,
                width: 160,
                height: 90,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                scrollSnapAlign: "start",
                border:
                  index === activeIndex ? "3px solid" : "3px solid transparent",
                borderColor:
                  index === activeIndex ? "primary.main" : "transparent",
                opacity: index === activeIndex ? 1 : 0.6,
                transition: "all 0.2s ease",
                "&:hover": { opacity: 1 },
                backgroundImage: `url(${video.thumbnail})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Youtube size={16} color="white" />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default Video;
